import { cn } from '@lib/cn';
import { Button, Chip, SelectField, TextField } from '@shared/ui';
import { useState } from 'react';

import {
    presetWindow,
    RANGE_PRESETS,
    type RangePreset,
    type ViewerFilters,
} from '../model/filters';
import { fromLocalInput, toLocalInput } from '../model/local-time';
import {
    FILTERABLE_REFUSAL_REASONS,
    PROVIDER_LABEL,
    REFUSAL_PROVIDERS,
    reasonLabel,
    type FilterableRefusalReason,
    type RefusalProvider,
} from '../model/reasons';

import type { ReactNode } from 'react';

type RangeChoice = RangePreset | 'custom';

const RANGE_LABEL: Record<RangeChoice, string> = {
    '24h': 'Last 24 hours',
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    custom: 'Custom range',
};

const CONTROL =
    'w-full rounded-lg border border-line bg-surface px-3 py-2 text-[13.5px] text-content outline-none focus:border-brand focus:ring-2 focus:ring-accent-surface';

/**
 * The filters, edited as a draft and written to the URL only on "Apply"
 * (design 04, W1): each read is an audited access with a rate limit, so a
 * keystroke in the domain field must not become a query. Typed values only;
 * there is no free-text search.
 */
export function RefusalsFilters({
    filters,
    onApply,
    exportControl,
}: {
    filters: ViewerFilters;
    onApply: (next: ViewerFilters) => void;
    exportControl: ReactNode;
}) {
    const [range, setRange] = useState<RangeChoice>(filters.from ? 'custom' : '24h');
    const [from, setFrom] = useState(filters.from ? toLocalInput(filters.from) : '');
    const [to, setTo] = useState(filters.to ? toLocalInput(filters.to) : '');
    const [reasons, setReasons] = useState<readonly FilterableRefusalReason[]>(filters.reasons);
    const [provider, setProvider] = useState<RefusalProvider | 'any'>(filters.provider ?? 'any');
    const [domain, setDomain] = useState(filters.domain ?? '');
    const [rangeError, setRangeError] = useState<string | undefined>();

    const toggleReason = (reason: FilterableRefusalReason) => {
        setReasons((current) =>
            current.includes(reason) ? current.filter((r) => r !== reason) : [...current, reason],
        );
    };

    const apply = () => {
        let window: { from?: string; to?: string };
        if (range === 'custom') {
            const start = fromLocalInput(from);
            const end = fromLocalInput(to);
            if (start === undefined || end === undefined || start >= end) {
                setRangeError('Choose a start before the end');
                return;
            }
            window = { from: start, to: end };
        } else {
            window = presetWindow(range, new Date());
        }
        setRangeError(undefined);
        const trimmedDomain = domain.trim().toLowerCase();
        onApply({
            ...window,
            ...(filters.type !== undefined ? { type: filters.type } : {}),
            ...(trimmedDomain !== '' ? { domain: trimmedDomain } : {}),
            reasons,
            ...(provider !== 'any' ? { provider } : {}),
        });
    };

    return (
        <div className="space-y-4 px-5 py-4" data-testid="unidentified-refusals-filters">
            <div className="grid gap-4 md:grid-cols-3">
                <SelectField
                    label="Range"
                    value={range}
                    onChange={setRange}
                    options={[...RANGE_PRESETS, 'custom' as const].map((value) => ({
                        value,
                        label: RANGE_LABEL[value],
                    }))}
                />
                <SelectField
                    label="Provider"
                    value={provider}
                    onChange={setProvider}
                    options={[
                        { value: 'any' as const, label: 'Any provider' },
                        ...REFUSAL_PROVIDERS.map((value) => ({
                            value,
                            label: PROVIDER_LABEL[value],
                        })),
                    ]}
                />
                <TextField
                    label="Domain"
                    value={domain}
                    onChange={setDomain}
                    placeholder="museum.org"
                    mono
                />
            </div>
            {range === 'custom' && (
                <div className="grid gap-4 md:grid-cols-2">
                    <LocalDateTime label="From" value={from} onChange={setFrom} />
                    <LocalDateTime label="To" value={to} onChange={setTo} />
                    {rangeError && (
                        <p role="alert" className="text-[12.5px] text-danger-text md:col-span-2">
                            {rangeError}
                        </p>
                    )}
                </div>
            )}
            <fieldset>
                <legend className="mb-2 text-[12.5px] font-semibold text-content-body">
                    Reason
                </legend>
                <div className="flex flex-wrap gap-2">
                    {FILTERABLE_REFUSAL_REASONS.map((reason) => (
                        <Chip
                            key={reason}
                            active={reasons.includes(reason)}
                            onClick={() => toggleReason(reason)}
                        >
                            {reasonLabel(reason)}
                        </Chip>
                    ))}
                </div>
            </fieldset>
            <div className="flex flex-wrap items-start gap-3">
                <Button onClick={apply}>Apply</Button>
                {exportControl}
            </div>
        </div>
    );
}

function LocalDateTime({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (next: string) => void;
}) {
    return (
        <label className="flex flex-col gap-1.5 text-[12.5px] font-semibold text-content-body">
            {label}
            <input
                type="datetime-local"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                className={cn(CONTROL, 'font-normal')}
            />
        </label>
    );
}
